import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const CartContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Iter 187 — Cart context now includes `customer_email` + automatic
 * abandoned-cart capture. When a shopper enters their email on a
 * checkout page (any component calls `setCustomerEmail(...)`), and
 * the cart has items, we POST /api/public/cart-abandoned so the
 * cart-recovery worker (see cart_recovery_worker.py) can email
 * them 1h/24h/48h later.
 *
 * Capture is debounced (2s) to avoid firing on every keystroke, and
 * de-duplicated per email — if a shopper submits the same email
 * twice with the same cart, the backend upserts on (site_id, email).
 */
export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [customerEmail, setCustomerEmail] = useState(() => localStorage.getItem('cart_email') || '');
  const captureTimer = useRef(null);
  const lastCapturedKey = useRef('');

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (customerEmail) localStorage.setItem('cart_email', customerEmail);
    else localStorage.removeItem('cart_email');
  }, [customerEmail]);

  /* ── Abandoned-cart capture ───────────────────────────────
     Fires when (email + items + subdomain) are all present.
     Debounced 2s to coalesce keystrokes. Uses subdomain from
     window.location.hostname; on the platform preview domain we
     use the first path segment `/site/<subdomain>/...` (best-
     effort — a checkout page can call `captureAbandoned()`
     directly with an explicit subdomain if the automatic one
     doesn't apply). */
  const inferSubdomain = useCallback(() => {
    try {
      const host = window.location.hostname;
      // Custom domain — use hostname as-is
      if (!host.includes('emergentagent.com') && !host.startsWith('localhost')) return host;
      // Preview: expect /site/<subdomain>/...
      const m = window.location.pathname.match(/^\/site\/([^/]+)/);
      return m ? m[1] : '';
    } catch { return ''; }
  }, []);

  const captureAbandoned = useCallback(async (opts = {}) => {
    const email = (opts.email || customerEmail || '').trim().toLowerCase();
    const site_subdomain = opts.site_subdomain || inferSubdomain();
    if (!email || !site_subdomain || items.length === 0) return { skipped: true };

    // Dedup: don't re-fire for the same (email, cart hash)
    const key = `${email}|${JSON.stringify(items)}`;
    if (key === lastCapturedKey.current) return { deduped: true };

    const total = items.reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1), 0);
    const payload = {
      customer_email: email,
      site_subdomain,
      items: items.map((it) => ({ name: it.name, qty: it.quantity, price: it.price })),
      total,
      checkout_url: opts.checkout_url || window.location.href,
    };
    try {
      const { data } = await axios.post(`${API}/public/cart-abandoned`, payload);
      lastCapturedKey.current = key;
      return { status: 'captured', ...data };
    } catch (e) {
      // Non-fatal: the checkout UX must never break because capture failed.
      return { error: e?.response?.data?.detail || 'capture failed' };
    }
  }, [customerEmail, items, inferSubdomain]);

  // Auto-fire (debounced) when email changes and there's a cart.
  useEffect(() => {
    if (captureTimer.current) clearTimeout(captureTimer.current);
    if (!customerEmail || items.length === 0) return;
    // Basic email sanity check before dispatching
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) return;
    captureTimer.current = setTimeout(() => { captureAbandoned(); }, 2000);
    return () => { if (captureTimer.current) clearTimeout(captureTimer.current); };
  }, [customerEmail, items, captureAbandoned]);

  const addItem = (product, size, quantity = 1) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(
        item => item.product_id === product.id && item.size === size
      );

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }

      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        size,
        quantity,
        image: product.images?.[0] || ''
      }];
    });
  };

  const removeItem = (productId, size) => {
    setItems(prev => prev.filter(
      item => !(item.product_id === productId && item.size === size)
    ));
  };

  const updateQuantity = (productId, size, quantity) => {
    if (quantity < 1) {
      removeItem(productId, size);
      return;
    }

    setItems(prev => prev.map(item =>
      item.product_id === productId && item.size === size
        ? { ...item, quantity }
        : item
    ));
  };

  const clearCart = () => {
    setItems([]);
    setCustomerEmail('');
    lastCapturedKey.current = '';
  };

  // Iter 187 — call this when a checkout completes successfully so the
  // worker won't send further reminder emails.
  const markCheckoutComplete = useCallback(async () => {
    const email = (customerEmail || '').trim().toLowerCase();
    const site_subdomain = inferSubdomain();
    if (!email || !site_subdomain) return;
    try {
      await axios.post(`${API}/public/cart-abandoned/recovered`, { customer_email: email, site_subdomain });
    } catch { /* swallow */ }
    clearCart();
  }, [customerEmail, inferSubdomain]);

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      total,
      itemCount,
      // Iter 187 additions
      customerEmail,
      setCustomerEmail,
      captureAbandoned,
      markCheckoutComplete,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
